import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold.tsx';
import { Clock } from '@sorare/core/src/atoms/icons/Clock.tsx';
import { Gift } from '@sorare/core/src/atoms/icons/Gift.tsx';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  BrandS,
  BrandXS,
  HeadlineL,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge/index.tsx';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig/index.tsx';
import { Avatar } from '@sorare/core/src/components/user/Avatar/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import {
  cardAttributes,
  fantasy,
  glossary,
} from '@sorare/core/src/lib/glossary.ts';

import type { ReferralMissionDetails_inviteTaskAsReferrer } from './__generated__/index.graphql.ts';

const Content = styled(Vertical).attrs({ gap: 6, center: true })`
  padding: var(--intermediate-unit) var(--double-unit);
`;
const StatsBar = styled(SBHorizontal)`
  width: 100%;
  backdrop-filter: blur(36px);
  background: var(--c-nl-500);
  border: 1px solid var(--c-nd-200);
  border-radius: var(--double-unit);
  padding: var(--unit) var(--triple-unit);
  overflow: hidden;
  white-space: nowrap;
`;
const MissionCard = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-200);
  position: relative;
  overflow: hidden;
  isolation: isolate;
  align-items: center;
`;
const RewardsNeonCard = styled(NeonEdge)`
  isolation: isolate;
  padding: calc(6 * var(--unit)) var(--double-unit) var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-set-surface-primary);
  background:
    linear-gradient(180deg, rgba(14, 14, 14, 0) 0%, #0e0e0e 60%),
    conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0)
      0 0 / 5px 5px;
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
`;
const GaugeWrapper = styled.div`
  position: relative;
  width: 100%;
`;
const GaugeBar = styled(Horizontal).attrs({ gap: 0 })`
  width: 100%;
  border-radius: var(--unit);
  overflow: hidden;
`;
const GaugeSegment = styled(Vertical)`
  flex: 1;
  height: 40px;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: var(--half-unit);
  background: var(--segment-bg);
  &:not(:last-child) {
    border-right: 1px solid rgba(var(--c-rgb-white), 0.07);
  }
`;
const GaugeMarkerWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--intermediate-unit));
`;
const LevelLabels = styled(Horizontal).attrs({ gap: 0 })`
  width: 100%;
`;
const LevelLabel = styled(Vertical).attrs({ center: true })`
  flex: 1;
`;
const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--c-nd-200);
`;
const SectionChevron = styled(ChevronDownBold)<{ $expanded?: boolean }>`
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
  flex-shrink: 0;
`;
const ExpandHeader = styled(ButtonBase)`
  align-items: center;
  display: flex;
  gap: var(--unit);
  z-index: 1;
`;
const StepsList = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--unit) 0;
`;
const StepRow = styled(SBHorizontal)`
  align-items: center;
  width: 100%;
  &.dimmed {
    opacity: 0.5;
  }
`;
const Nickname = styled(HeadlineL)`
  word-break: break-word;
`;

type Step = ReferralMissionDetails_inviteTaskAsReferrer['steps'][number];

const StepRewardRow = ({
  step,
  index,
  currentStepIndex,
}: {
  step: Step;
  index: number;
  currentStepIndex: number;
}) => {
  const isCompleted =
    step.aasmState === ManagerProgressionTaskState.COMPLETED ||
    step.aasmState === ManagerProgressionTaskState.CLAIMED;
  const rewardConfig = step.rewardConfigs?.at(0);

  return (
    <StepRow className={classNames({ dimmed: index > currentStepIndex })}>
      <Vertical gap={0.5}>
        {!!step.title && (
          <BrandXS
            color={
              isCompleted
                ? 'var(--c-nd-400)'
                : currentStepIndex === index
                  ? 'var(--c-set-surface-primary-text)'
                  : 'var(--c-set-surface-secondary-text)'
            }
          >
            {step.title}
          </BrandXS>
        )}
      </Vertical>
      <div className={classNames({ dimmed: isCompleted })}>
        {rewardConfig ? (
          <AnyRewardConfig
            anyRewardConfig={rewardConfig}
            variant={Variant.TEXT}
          />
        ) : (
          <LabelM color="var(--c-nd-500)">–</LabelM>
        )}
      </div>
    </StepRow>
  );
};

type Props = {
  task: ReferralMissionDetails_inviteTaskAsReferrer;
};

export const ReferralMissionDetails = ({ task }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { progress, steps, endDate, referral } = task;
  const { referee } = referral;

  const currentStepIndex = Math.min(progress, steps.length - 1);
  const currentStep = steps[currentStepIndex];
  const currentStepReward = currentStep?.rewardConfigs?.at(0);

  const stepWidthRatio = steps.length > 0 ? 100 / steps.length : 100;

  return (
    <Content>
      <Vertical gap={3} center style={{ width: '100%' }}>
        <Vertical gap={1.5} center>
          <Avatar user={referee} size={10} rounded />
          <Nickname bold className="text-center">
            {referee.nickname}
          </Nickname>
        </Vertical>

        <StatsBar>
          <Vertical center gap={0}>
            <LabelS color="rgba(var(--c-rgb-white), 0.5)">
              <FormattedMessage {...fantasy.joined} />
            </LabelS>
            <LabelM bold>
              {referee.createdAt ? (
                <FormattedDate
                  value={referee.createdAt}
                  day="2-digit"
                  month="2-digit"
                  year="2-digit"
                />
              ) : (
                '–'
              )}
            </LabelM>
          </Vertical>
          <Vertical center gap={0}>
            <LabelS color="rgba(var(--c-rgb-white), 0.5)">
              <FormattedMessage defaultMessage="Cards owned" />
            </LabelS>
            <LabelM bold>{referee.cardCounts?.total ?? '–'}</LabelM>
          </Vertical>
          <Vertical center gap={0}>
            <LabelS color="rgba(var(--c-rgb-white), 0.5)">
              <FormattedMessage {...cardAttributes.level} />
            </LabelS>
            <LabelM bold>{progress}</LabelM>
          </Vertical>
        </StatsBar>
      </Vertical>
      {!!currentStep && (
        <MissionCard>
          {!!currentStepReward && (
            <AnyRewardConfig
              anyRewardConfig={currentStepReward}
              variant={Variant.CHIP}
              textVariantSize="lg"
            />
          )}
          <Vertical gap={0.5} style={{ flex: 1, minWidth: 0 }}>
            <BrandXS color="var(--c-set-surface-primary)">
              <FormattedMessage defaultMessage="Ongoing mission" />
            </BrandXS>
            <LabelL bold>{currentStep.title || currentStep.description}</LabelL>
          </Vertical>
        </MissionCard>
      )}
      <Vertical gap={2} style={{ width: '100%' }}>
        <Vertical gap={1}>
          <Horizontal gap={0.5}>
            <Gift size={2} color="var(--c-set-surface-primary)" />
            <BrandS>
              <FormattedMessage {...glossary.rewards} />
            </BrandS>
          </Horizontal>
          <BodyM>
            <FormattedMessage defaultMessage="You'll earn more rewards as your friend levels up!" />
          </BodyM>
        </Vertical>

        <RewardsNeonCard
          color="var(--c-set-surface-primary)"
          side="top"
          spread="50%"
          haloOpacity={0.5}
        >
          <SBHorizontal>
            <Horizontal gap={1} center>
              <LabelL bold>
                <FormattedMessage
                  defaultMessage="Lvl {level}"
                  values={{ level: progress }}
                />
              </LabelL>
              {!!currentStep?.title && (
                <BrandXS color="var(--c-set-surface-primary)">
                  {currentStep.title}
                </BrandXS>
              )}
            </Horizontal>
            {!!endDate && (
              <Horizontal gap={0.5} center>
                <Clock size={1.5} />
                <BrandXS color="var(--c-onBase-caution)">
                  <TimeLeft time={endDate} />{' '}
                  <FormattedMessage defaultMessage="left" />
                </BrandXS>
              </Horizontal>
            )}
          </SBHorizontal>

          <Vertical gap={1}>
            <GaugeWrapper>
              <GaugeBar>
                {steps.map((step, i) => {
                  const completed = i < progress;
                  const current = i === progress;
                  const active = completed || current;

                  let segmentBg = 'var(--c-nd-50)';
                  if (active) {
                    segmentBg = 'var(--c-set-surface-primary)';
                  }
                  if (current) {
                    segmentBg = 'var(--c-set-surface-primary-text)';
                  }

                  return (
                    <GaugeSegment
                      key={step.id}
                      style={
                        {
                          '--segment-bg': segmentBg,
                        } as CSSProperties
                      }
                    >
                      {!!step.rewardConfigs?.at(0) && (
                        <AnyRewardConfig
                          anyRewardConfig={step.rewardConfigs[0]}
                          variant={Variant.TEXT}
                          hideText
                        />
                      )}
                    </GaugeSegment>
                  );
                })}
              </GaugeBar>
              <GaugeMarkerWrapper
                style={{
                  left: `calc(${progress * stepWidthRatio + stepWidthRatio / 2}% - 8px)`,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="64"
                  viewBox="0 0 16 64"
                  fill="none"
                >
                  <path
                    d="M6.29086 11.3589L6.29086 56.5805C6.29086 57.3644 6.92641 58 7.7104 58C8.49439 58 9.12995 57.3645 9.12995 56.5805L9.12995 11.4032C9.12995 10.4277 9.4791 9.48434 10.1142 8.74389L10.5949 8.18357C12.6785 5.75443 10.9525 2 7.75216 2C4.60121 2 2.85862 5.654 4.84188 8.1025L5.3672 8.75106C5.96479 9.48883 6.29086 10.4094 6.29086 11.3589Z"
                    fill="white"
                  />
                </svg>
              </GaugeMarkerWrapper>
            </GaugeWrapper>
            <LevelLabels>
              {steps.map((_, i) => {
                const completed = i < progress;
                return (
                  <LevelLabel key={i}>
                    {completed ? (
                      <FontAwesomeIcon
                        icon={iconCheck}
                        color="var(--c-set-surface-primary)"
                      />
                    ) : (
                      <BrandXS
                        color={
                          i <= progress
                            ? 'var(--c-set-surface-primary)'
                            : 'var(--c-nd-400)'
                        }
                      >
                        {i}
                      </BrandXS>
                    )}
                  </LevelLabel>
                );
              })}
            </LevelLabels>
          </Vertical>

          <Divider />
          <Vertical>
            <ExpandHeader onClick={() => setExpanded(prev => !prev)}>
              <SectionChevron $expanded={expanded} />
              <LabelM bold>
                <FormattedMessage {...glossary.rewards} />
              </LabelM>
            </ExpandHeader>

            <Collapse
              open={expanded}
              gap={0}
              collapsedContent={
                !!currentStep && (
                  <StepRewardRow
                    step={currentStep}
                    index={currentStepIndex}
                    currentStepIndex={currentStepIndex}
                  />
                )
              }
            >
              <StepsList>
                {steps.map((step, i) => (
                  <StepRewardRow
                    key={step.id}
                    step={step}
                    index={i}
                    currentStepIndex={currentStepIndex}
                  />
                ))}
              </StepsList>
            </Collapse>
          </Vertical>
        </RewardsNeonCard>
      </Vertical>
    </Content>
  );
};

ReferralMissionDetails.fragments = {
  inviteTaskAsReferrer: gql`
    fragment ReferralMissionDetails_inviteTaskAsReferrer on InviteTaskAsReferrer {
      id
      aasmState
      progress
      endDate
      title
      description
      steps {
        id
        aasmState
        title
        description
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
      referral {
        id
        referee {
          slug
          nickname
          createdAt
          cardCounts(sport: $sport) {
            total
          }
          ...Avatar_user
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<ReferralMissionDetails_inviteTaskAsReferrer>,
};
